import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Swords } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { anchorPoint } from "../fxAnchors";
import { ROLES } from "../gameData";
import type { FxEffect } from "../useGameDeltas";
import { useGameDeltas } from "../useGameDeltas";
import type { GameState, Role } from "../types";

/**
 * The animation layer — VEIL's replacement for the log it deliberately does not
 * have. Coins fly between the seats that gained and lost them, a card comes out
 * of anyone who claims a role, a challenge is held for a beat before its
 * verdict lands, and a lost Influence is shown at the seat that lost it.
 *
 * Two hard rules govern this file:
 *
 *  1. It NEVER gates incoming state. `setGame` keeps applying snapshots the
 *     instant they arrive; a bot's 700ms alarm can land mid-beat and the table
 *     underneath simply updates. Everything here is presentational, owned by
 *     local state, bounded by a hard timeout, and dismissable with a tap.
 *  2. A bluff and a truthful claim animate IDENTICALLY. The client holds its
 *     own hand and could compute the difference; any tell would leak it.
 */

interface Props {
  game: GameState;
  meId: string;
}

interface ChallengeBeat {
  key: string;
  role: Role;
  proved: boolean;
  /** You are the claimant or the challenger — hold the full beat. */
  full: boolean;
  caller: string;
}

export function TableFx({ game, meId }: Props) {
  const reduced = useReducedMotion() ?? false;
  const layerRef = useRef<HTMLDivElement | null>(null);
  const { effects, dismiss, flush } = useGameDeltas(game, meId);
  const [beat, setBeat] = useState<ChallengeBeat | null>(null);
  const beatTimer = useRef<number | null>(null);
  const gameOver = game.phase === "game_over";

  const nameOf = useCallback(
    (playerId: string) => game.players.find((player) => player.id === playerId)?.name ?? "A player",
    [game.players]
  );

  const clearBeat = useCallback(() => {
    if (beatTimer.current !== null) {
      window.clearTimeout(beatTimer.current);
      beatTimer.current = null;
    }
    setBeat(null);
  }, []);

  useEffect(() => () => clearBeat(), [clearBeat]);

  // Promote a challenge delta into a locally-owned beat. Local, not derived:
  // the server resolved the challenge in the same message that opened it, so
  // the suspense is entirely fabricated here and has to survive every snapshot
  // that lands while it plays.
  useEffect(() => {
    const challenge = effects.find(
      (effect): effect is Extract<FxEffect, { kind: "challenge" }> => effect.kind === "challenge"
    );
    if (!challenge) return;
    dismiss(challenge.id);

    const mine = challenge.claimantId === meId;
    const theirs = challenge.challengerId === meId;
    const full = mine || theirs;
    const caller = mine
      ? `${nameOf(challenge.challengerId)} calls you out`
      : theirs
        ? `You call out ${nameOf(challenge.claimantId)}`
        : `${nameOf(challenge.challengerId)} calls out ${nameOf(challenge.claimantId)}`;

    setBeat({ key: challenge.id, role: challenge.role, proved: challenge.proved, full, caller });
    if (beatTimer.current !== null) window.clearTimeout(beatTimer.current);
    beatTimer.current = window.setTimeout(
      () => {
        beatTimer.current = null;
        setBeat(null);
      },
      reduced ? 1000 : full ? 1750 : 1100
    );
    // The cleanup deliberately lives in the unmount effect above: returning a
    // clear() from here would kill the running beat the moment `effects`
    // changes for an unrelated reason.
  }, [effects, dismiss, meId, nameOf, reduced]);

  // The victory sequence owns the screen from here. Anything still queued would
  // only fight the ink wipe, and a stale beat must never outlive the game.
  useEffect(() => {
    if (!gameOver) return;
    clearBeat();
    flush();
  }, [gameOver, clearBeat, flush]);

  return (
    <>
      <div className="fx-layer" ref={layerRef} aria-hidden="true">
        {(gameOver ? [] : effects).map((effect) => {
          switch (effect.kind) {
            case "coin":
              return (
                <CoinFlight
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={dismiss}
                />
              );
            case "claim":
              return (
                <ClaimCard
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={dismiss}
                />
              );
            case "reveal":
              return (
                <RevealCard
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={dismiss}
                />
              );
            case "out":
              return (
                <OutStamp
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={dismiss}
                />
              );
            default:
              return null;
          }
        })}
      </div>

      <AnimatePresence>
        {beat && <ChallengeStage key={beat.key} beat={beat} reduced={reduced} onDismiss={clearBeat} />}
      </AnimatePresence>
    </>
  );
}

/* ------------------------------------------------------------------ parts -- */

type LayerRef = React.MutableRefObject<HTMLDivElement | null>;

interface FxProps<T extends FxEffect> {
  effect: T;
  layerRef: LayerRef;
  reduced: boolean;
  onDone: (id: string) => void;
}

interface Placement {
  x: number;
  y: number;
  /** How far above the anchor this sprite may travel before it leaves the screen. */
  rise: number;
}

/**
 * Measure an anchor once, at fire time, in the layer's own coordinate space,
 * and work out how much room the sprite actually has around it.
 *
 * Both clamps are load-bearing on a phone. `half.x` keeps a card hung off the
 * outermost seat (~39px from the edge) out of `.app-shell`'s clip, and `rise`
 * collapses toward 0 for the seats at the top of the arc, which sit barely a
 * card's height below the header — an unbounded rise decapitates them.
 */
function usePlacement(
  anchor: string,
  layerRef: LayerRef,
  half: { x: number; y: number },
  wantRise = 0
): Placement | null {
  return useMemo(() => {
    const bounds = layerRef.current?.getBoundingClientRect() ?? null;
    const point = anchorPoint(anchor, bounds);
    if (!point || !bounds) return null;
    const header = document.querySelector(".game-header")?.getBoundingClientRect();
    const ceiling = (header ? header.bottom - bounds.top : 0) + 6;
    const edge = half.x + 4;
    return {
      x: Math.min(Math.max(point.x, edge), Math.max(edge, bounds.width - edge)),
      y: Math.max(point.y, ceiling + half.y),
      rise: Math.max(0, Math.min(wantRise, point.y - half.y - ceiling))
    };
  }, [anchor, layerRef, half.x, half.y, wantRise]);
}

/** Nothing to aim at (a seat that just left, a first paint) — retire quietly. */
function useAbortIfUnanchored(missing: boolean, id: string, onDone: (id: string) => void) {
  useEffect(() => {
    if (missing) onDone(id);
  }, [missing, id, onDone]);
}

function CoinFlight({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "coin" }>>) {
  const plan = useMemo(() => {
    const origin = layerRef.current?.getBoundingClientRect() ?? null;
    const from = anchorPoint(effect.from, origin);
    const to = anchorPoint(effect.to, origin);
    if (!from || !to) return null;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    // Perpendicular unit vector: the arc bows out to one side of the straight
    // line, so a fistful of coins reads as a throw rather than a laser.
    const nx = -dy / length;
    const ny = dx / length;
    return {
      from,
      to,
      coins: Array.from({ length: effect.count }, (_, index) => {
        const lift = Math.min(70, length * 0.28) + index * 5;
        return { index, dx, dy, mx: dx * 0.5 + nx * lift, my: dy * 0.5 + ny * lift };
      })
    };
  }, [effect, layerRef]);

  useAbortIfUnanchored(!plan, effect.id, onDone);
  if (!plan) return null;

  if (reduced) {
    return (
      <motion.span
        className="fx-pulse"
        style={{ left: plan.to.x, top: plan.to.y }}
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: 1.7, opacity: [0, 0.9, 0] }}
        transition={{ duration: 0.3 }}
        onAnimationComplete={() => onDone(effect.id)}
      />
    );
  }

  return (
    <>
      {plan.coins.map((coin) => (
        <motion.span
          key={coin.index}
          className="fx-coin"
          style={{ left: plan.from.x, top: plan.from.y }}
          initial={{ x: 0, y: 0, scale: 0.4, opacity: 0 }}
          animate={{
            x: [0, coin.mx, coin.dx],
            y: [0, coin.my, coin.dy],
            scale: [0.4, 1.05, 0.55],
            opacity: [0, 1, 0],
            rotate: [0, 200, 380]
          }}
          transition={{
            duration: 0.52,
            delay: coin.index * 0.055,
            times: [0, 0.55, 1],
            ease: [0.22, 0.9, 0.28, 1]
          }}
          onAnimationComplete={() => {
            if (coin.index === plan.coins.length - 1) onDone(effect.id);
          }}
        />
      ))}
    </>
  );
}

const CARD_HALF = { x: 34, y: 46 };

function ClaimCard({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "claim" }>>) {
  const place = usePlacement(effect.anchor, layerRef, CARD_HALF, 70);
  const role = ROLES[effect.role];
  useAbortIfUnanchored(!place, effect.id, onDone);
  if (!place) return null;
  const top = place.rise;

  return (
    <motion.span
      className="fx-claim-card"
      style={{ left: place.x, top: place.y, "--role-color": role.color } as React.CSSProperties}
      initial={reduced ? { opacity: 0, y: -top } : { y: 8, scale: 0.14, rotate: -18, opacity: 0 }}
      animate={
        reduced
          ? { opacity: [0, 1, 1, 0], y: -top }
          : {
              y: [8, -top, -top * 0.9, -top * 0.9, -top - 14],
              scale: [0.14, 1.06, 1, 1, 0.74],
              rotate: [-18, -5, -4, -4, -4],
              opacity: [0, 1, 1, 1, 0]
            }
      }
      transition={
        reduced
          ? { duration: 0.9, times: [0, 0.18, 0.76, 1] }
          : {
              duration: 1.5,
              times: [0, 0.22, 0.31, 0.8, 1],
              ease: ["easeOut", "easeOut", "linear", "easeIn"]
            }
      }
      onAnimationComplete={() => onDone(effect.id)}
    >
      <img src={role.image} alt="" draggable={false} />
      <b>{role.name}</b>
    </motion.span>
  );
}

function RevealCard({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "reveal" }>>) {
  const place = usePlacement(effect.anchor, layerRef, CARD_HALF, 46);
  const role = ROLES[effect.role];
  useAbortIfUnanchored(!place, effect.id, onDone);
  if (!place) return null;
  const top = place.rise;

  return (
    <motion.span
      className="fx-claim-card fx-claim-card--lost"
      style={{ left: place.x, top: place.y, "--role-color": role.color } as React.CSSProperties}
      initial={reduced ? { opacity: 0, y: -top } : { y: 0, scale: 0.32, rotate: 0, opacity: 0 }}
      animate={
        reduced
          ? { opacity: [0, 1, 1, 0], y: -top }
          : {
              y: [0, -top, -top * 0.9, -top * 0.34],
              scale: [0.32, 1.05, 1, 0.86],
              rotate: [0, 5, 4, 13],
              opacity: [0, 1, 1, 0]
            }
      }
      transition={
        reduced
          ? { duration: 0.9, times: [0, 0.18, 0.76, 1] }
          : { duration: 1.35, times: [0, 0.2, 0.72, 1], ease: ["easeOut", "linear", "easeIn"] }
      }
      onAnimationComplete={() => onDone(effect.id)}
    >
      <img src={role.image} alt="" draggable={false} />
      <b>{role.name}</b>
    </motion.span>
  );
}

function OutStamp({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "out" }>>) {
  const place = usePlacement(effect.anchor, layerRef, { x: 44, y: 18 });
  useAbortIfUnanchored(!place, effect.id, onDone);
  if (!place) return null;

  return (
    <motion.span
      className="fx-out"
      style={{ left: place.x, top: place.y }}
      initial={reduced ? { opacity: 0, rotate: -9 } : { scale: 2.4, opacity: 0, rotate: -16 }}
      animate={
        reduced
          ? { opacity: [0, 1, 1, 0], rotate: -9 }
          : { scale: [2.4, 1, 1, 1.12], opacity: [0, 1, 1, 0], rotate: [-16, -9, -9, -9] }
      }
      transition={
        reduced
          ? { duration: 0.9, times: [0, 0.18, 0.76, 1] }
          : { duration: 1.35, times: [0, 0.13, 0.78, 1], ease: ["easeOut", "linear", "easeIn"] }
      }
      onAnimationComplete={() => onDone(effect.id)}
    >
      OUT
    </motion.span>
  );
}

/**
 * The held beat. The claimed card lands centre-screen ringed in coral, shakes
 * while the table decides, and only then does the verdict stamp fall.
 *
 * The verdict element is mounted from the start at opacity 0 so it reserves its
 * row in the grid — otherwise the card would jump when the stamp arrives.
 */
function ChallengeStage({
  beat,
  reduced,
  onDismiss
}: {
  beat: ChallengeBeat;
  reduced: boolean;
  onDismiss: () => void;
}) {
  const role = ROLES[beat.role];
  const [verdict, setVerdict] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(
      () => {
        setVerdict(true);
        navigator.vibrate?.([30, 40, 60]);
      },
      reduced ? 480 : beat.full ? 900 : 620
    );
    return () => window.clearTimeout(timer);
  }, [reduced, beat.full]);

  return (
    <motion.div
      className="challenge-stage"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduced ? 0.12 : 0.2 }}
      // A tap always cuts it short. Never trap an impatient player behind a
      // flourish — the real game state is already live underneath.
      onPointerDown={onDismiss}
    >
      <motion.span
        className="challenge-stage__caller"
        initial={reduced ? { opacity: 0 } : { y: -12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.05, duration: 0.2 }}
      >
        <Swords size={15} strokeWidth={2.6} />
        {beat.caller}
      </motion.span>

      <motion.span
        initial={reduced ? { opacity: 0 } : { scale: 0.55, opacity: 0 }}
        animate={reduced ? { opacity: 1 } : { scale: [0.55, 1.08, 1], opacity: 1 }}
        transition={reduced ? { duration: 0.16 } : { duration: 0.28, times: [0, 0.72, 1] }}
      >
        <motion.span
          className="challenge-stage__card"
          style={{ "--role-color": role.color } as React.CSSProperties}
          animate={
            reduced
              ? undefined
              : { x: [0, -7, 6, -5, 4, -2, 0], rotate: [0, -1.8, 1.5, -1.1, 0.8, -0.4, 0] }
          }
          transition={
            reduced ? undefined : { delay: 0.16, duration: 0.34, repeat: 2, ease: "easeInOut" }
          }
        >
          <img src={role.image} alt="" draggable={false} />
          <b>{role.name}</b>
        </motion.span>
      </motion.span>

      <motion.span
        className={
          beat.proved
            ? "challenge-stage__verdict"
            : "challenge-stage__verdict challenge-stage__verdict--bluff"
        }
        initial={{ scale: 2.2, opacity: 0, rotate: -8 }}
        animate={
          verdict ? { scale: 1, opacity: 1, rotate: -8 } : { scale: 2.2, opacity: 0, rotate: -8 }
        }
        transition={
          reduced ? { duration: 0.16 } : { type: "spring", stiffness: 520, damping: 14 }
        }
      >
        {beat.proved ? "Proven" : "Bluff"}
      </motion.span>
    </motion.div>
  );
}
